import { SidebarMenu } from "components";
import { useRef, useState, useEffect } from "react";
import SellersOrders from "../components/SellersOrders";
import CustomersVisitControl from "../components/CustomersVisitControl";
import { formatDateToISODateString, formatISODateStringToDate, exportToExcel } from "utils";
import axios from "axios";
import { useAppSelector } from "hooks";
import { CircularProgress, Box, Tabs, Tab, Button, Popover, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Checkbox, Divider, Typography, TextField, Stack, Grid, Paper, InputAdornment } from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import ViewVisit from "../components/ViewVisit";
import ViewCostumer from "../components/ViewCostumer";

function DateRangeControl({
	systemMin,
	today,
	uiStart,
	setUiStart,
	uiEnd,
	setUiEnd,
	onApply
}: {
	systemMin: Date;
	today: Date;
	uiStart: Date;
	setUiStart: (d: Date) => void;
	uiEnd: Date;
	setUiEnd: (d: Date) => void;
	onApply: () => void;
}) {
	const MAX_RANGE_MONTHS = 6;

	const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

	function clampToSystem(date: Date) {
		let x = toMidnight(date);
		const min = toMidnight(systemMin);
		const max = toMidnight(today);
		if (x < min) x = min;
		if (x > max) x = max;
		return x;
	}

	function addMonths(date: Date, months: number) {
		const d = new Date(date.getTime());
		const day = d.getDate();
		d.setMonth(d.getMonth() + months);
		// Handle month overflow (e.g., March 31 -> add 1 month -> April 30)
		if (d.getDate() < day) {
			d.setDate(0); // go to last day of previous month
		}
		return d;
	}

	function normalizeWithAnchor(start: Date, end: Date, anchor: "start" | "end") {
		let s = clampToSystem(start);
		let e = clampToSystem(end);

		if (anchor === "start") {
			// cap end to start + MAX_RANGE_MONTHS
			const maxEnd = addMonths(s, MAX_RANGE_MONTHS);
			const cappedMaxEnd = maxEnd > today ? today : maxEnd;
			if (e < s) e = cappedMaxEnd;
			if (e > cappedMaxEnd) e = cappedMaxEnd;
		} else {
			// ensure start is valid (>= systemMin) and end - start <= MAX_RANGE_MONTHS
			const minStart = addMonths(e, -MAX_RANGE_MONTHS);
			const cappedMinStart = minStart < systemMin ? systemMin : minStart;

			// Si start es mayor que end, ajustarlo
			if (s > e) {
				s = cappedMinStart;
			} else if (s < cappedMinStart) {
				// Solo ajustar si el rango excede los 6 meses
				s = cappedMinStart;
			}
			// Si start >= systemMin y está dentro del rango de 6 meses, mantenerlo
		}

		return [s, e] as const;
	}

	const handleStartInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(picked, uiEnd, "start");
		setUiStart(s);
		setUiEnd(e);
	};

	const handleEndInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
		const raw = ev.target.value;
		const picked = formatISODateStringToDate(raw, false);
		if (!picked) return;
		const [s, e] = normalizeWithAnchor(uiStart, picked, "end");
		setUiStart(s);
		setUiEnd(e);
	};

	// compute dynamic min/max for inputs to reflect 6-month constraint
	const computedMaxEnd = (() => {
		return today
	})();

	// const computedMinStart = (() => {
	// 	const minStart = addMonths(uiEnd, -MAX_RANGE_MONTHS);
	// 	return minStart < systemMin ? systemMin : minStart;
	// })();

	return (
		<Stack direction="row" spacing={3} alignItems="center">
			<TextField
				type="date"
				label="Fecha de inicio"
				variant="outlined"
				InputLabelProps={{ shrink: true }}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<CalendarMonth color="primary" />
						</InputAdornment>
					),
				}}
				inputProps={{
					min: formatDateToISODateString(systemMin),
					max: formatDateToISODateString(uiEnd),
				}}
				value={formatDateToISODateString(uiStart)}
				onChange={handleStartInput}
				size="medium"
				sx={{ minWidth: 220 }}
			/>

			<TextField
				type="date"
				label="Fecha de fin"
				variant="outlined"
				InputLabelProps={{ shrink: true }}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<CalendarMonth color="primary" />
						</InputAdornment>
					),
				}}
				inputProps={{
					min: formatDateToISODateString(systemMin),
					max: formatDateToISODateString(computedMaxEnd),
				}}
				value={formatDateToISODateString(uiEnd)}
				onChange={handleEndInput}
				size="medium"
				sx={{ minWidth: 220 }}
			/>

			<Button variant="contained" color="primary" onClick={onApply}>
				Actualizar
			</Button>
		</Stack>
	);
}

export const SellersControlPage = () => {
	const { token } = useAppSelector((state) => state.user);
	const today = new Date();

	// Calcular la fecha mínima del sistema (6 meses atrás)
	const systemMin = (() => {
		const d = new Date(today);
		d.setMonth(d.getMonth() - 6);
		return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
	})();

	// Inicializar el rango por defecto (último mes)
	const defaultStart = (() => {
		const d = new Date(today);
		// d.setMonth(d.getMonth() - 1);
		d.setDate(1)
		return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
	})();

	const [startDateSearchFilter, setStartDateSearchFilter] = useState<Date>(defaultStart);
	const [finishDateSearchFilter, setFinishDateSearchFilter] = useState<Date>(new Date());

	const contentRef = useRef<HTMLDivElement | null>(null);
	const [selectedVendors, setSelectedVendors] = useState<string[] | null>(null);
	const [vendorList, setVendorList] = useState<Array<{ code: string; name: string, checked: boolean }>>([]);
	const [loader, setLoader] = useState<boolean>(true);
	const [dataReport, setDataReport] = useState<any>({});
	const [backupDataReport, setBackupDataReport] = useState<any>({});
	const [activeReportTab, setActiveReportTab] = useState<"visits" | "orders" | "viewCustomer" | "viewVisit">("visits");
	const [vendorAnchorEl, setVendorAnchorEl] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (token) {
			const controller = new AbortController();
			getDataInitial(controller.signal);
			return () => controller.abort();
		}
	}, []); // eslint-disable-line

	const getDataInitial = async (signal: AbortSignal) => {
		setLoader(true);
		const response = await axios.get(`${process.env.REACT_APP_SERVICE_URL}/documents/get/v2/compliance/range`, {
			params: {
				dayInit: formatDateToISODateString(startDateSearchFilter),
				dayEnd: formatDateToISODateString(finishDateSearchFilter),
			},
			headers: {
				Authorization: `UserToken ${token || ""}`,
			},
			signal,
		});

		const listUser = (response.data?.data?.ordersResume || []).map((item: any) => ({
			code: item.userId,
			name: item.vendorName,
			checked: true,
		}));

		const withAll = [{ code: "all", name: "Todos", checked: true }, ...listUser];

		setVendorList(withAll);
		setDataReport(response.data.data || {})
		setBackupDataReport(response.data.data || {})
		setLoader(false);
	}

	const openVendorPopover = (ev: React.MouseEvent<HTMLElement>) => setVendorAnchorEl(ev.currentTarget);
	const closeVendorPopover = () => setVendorAnchorEl(null);
	const vendorPopoverOpen = Boolean(vendorAnchorEl);

	const toggleVendor = (code: string) => {
		if (code === "all") {
			const allChecked = vendorList.every(v => v.checked);
			const updatedList = vendorList.map(v => ({ ...v, checked: !allChecked }));
			setVendorList(updatedList);
			setSelectedVendors(!allChecked ? [] : null);
			setDataReport((prev: any) => !allChecked ? backupDataReport : { ...prev, details: [], ordersResume: [] });
			return;
		} else {
			const updatedList = vendorList.map(v => {
				if (v.code === code) return { ...v, checked: !v.checked };
				else if (v.code === "all") return { ...v, checked: false };
				return v;
			});
			setVendorList(updatedList);
			const selected = updatedList.filter(v => v.code !== "all" && v.checked).map(v => v.code);
			setSelectedVendors(selected.length === updatedList.length - 1 ? null : selected);
			const filteredData = (Array.isArray(backupDataReport?.ordersResume) ? backupDataReport.ordersResume : []).filter((item: any) => {
				const codeCandidates = [item.userId, item.salesPersonCode, item.vendorCode, item.vendorId].filter(Boolean);
				return selected.includes(String(codeCandidates[0]));
			});
			const ordersFilterData = (Array.isArray(backupDataReport?.details) ? backupDataReport.details : []).filter((item: any) => {
				const codeCandidates = [item.userId, item.salesPersonCode, item.vendorCode, item.vendorId].filter(Boolean);
				return selected.includes(String(codeCandidates[0]));
			});
			setDataReport((prev: any) => selected.length === updatedList.length - 1 ? backupDataReport : { ...prev, details: ordersFilterData, ordersResume: filteredData });
			return;
		}
	};

	const handleExportExcel = () => {
		const reportDuplicate = dataReport.details.map((item: any) => {
			const user = dataReport.ordersResume?.find((u: any) => u.userId === item.userId);
			return {
				"Ruta": user?.router,
				"Vendedor": user?.vendorName,
				"Fecha de la visita": new Date(item.created_at).toLocaleDateString(),
				"Hora de la visita": new Date(item.created_at).toLocaleTimeString(),
				"Proximidad": item.showFlagDistance ? "No" : "Sí",
				"Codigo de cliente": item.cardCode,
				"Razon social": item.cardName,
				"Nombre comercial": item.cardFName ?? "",
				"Venta efectiva": (() => {
					if (item.notesTracking?.notes) return "Orden de no compra"
					else if (item.canceled) return "Orden cancelada"
					else if (item.saveOrderSap) return "Orden registrada"
					else return "Orden pendiente de guardar"
				})(),
			}
		})
		const payloads = [
			{
				data: dataReport.ordersResume.map((item: any) => ({
					"Ruta": item.router,
					"Vendedor": item.vendorName,
					"# Vistas Registradas": item.visitClient,
					"AVG Visitas por día": item.avgVisitPerDay,
					"# Clientes Visitados": item.visitClient,
					"# Clientes Asignados": item.allsClients,
					"# Clientes pendientes": item.allsClients - item.visitClient,
					"Efectividad de Visita (%)": item.allsClients > 0 ? ((item.visitClient / item.allsClients) * 100).toFixed(2) : "0.00",
				})),
				sheetName: "Vista general"
			}, {
				data: reportDuplicate.map((item: any) => ({
					...item,
					"Venta efectiva": item["Venta efectiva"],
				})),
				sheetName: "Vista visita"
			}, {
				data: reportDuplicate.map((item: any) => ({
					"Ruta": item.Ruta,
					"Vendedor": item.Vendedor,
					"Codigo de cliente": item["Codigo de cliente"],
					"Razon social": item["Razon social"],
					"Nombre comercial": item["Nombre comercial"],
					"Venta efectiva": item["Venta efectiva"],
				})),
				sheetName: "Vista clientes"
			}, {
				data: [{
					"Clientes visitados": dataReport.ordersResume.reduce((acc: number, curr: any) => acc + (Number(curr.visitClient) || 0), 0),
					"Clientes pendientes": dataReport.ordersResume.reduce((acc: number, curr: any) => acc + ((Number(curr.allsClients) || 0) - (Number(curr.visitClient) || 0)), 0),
				}],
				sheetName: "Efectividad vendedores"
			}
		]
		exportToExcel("reporte_vendedores_" + new Date().toISOString(), payloads);
	};

	return (
		<Box ref={contentRef}>
			<Grid container sx={{ height: '100vh' }}>
				<Grid size={2} sx={{ borderRight: 2, borderColor: 'divider' }}>
					<SidebarMenu pageName="sellers_control" />
				</Grid>

				<Grid size={10} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
					<Paper sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 2, borderColor: 'divider', px: 2, py: 2, borderRadius: 0 }} elevation={0}>
						<Box>
							<DateRangeControl
								systemMin={systemMin}
								today={today}
								uiStart={startDateSearchFilter}
								setUiStart={setStartDateSearchFilter}
								uiEnd={finishDateSearchFilter}
								setUiEnd={setFinishDateSearchFilter}
								onApply={() => {
									const controller = new AbortController();
									getDataInitial(controller.signal);
								}}
							/>
						</Box>

						<Stack direction="row" spacing={2} alignItems="center">
							<Box>
								<Button variant="outlined" onClick={openVendorPopover}>
									Vendedores {selectedVendors === null ? "(Todos)" : `(${selectedVendors.length})`}
								</Button>
								<Popover
									open={vendorPopoverOpen}
									anchorEl={vendorAnchorEl}
									onClose={closeVendorPopover}
									anchorOrigin={{
										vertical: 'bottom',
										horizontal: 'left',
									}}
								>
									<Box sx={{ width: 390, maxHeight: 360, overflow: 'auto' }}>
										<List dense>
											<ListItem key="label">
												<ListItemText>
													<Typography variant="subtitle1">Filtrar por vendedor</Typography>
												</ListItemText>
											</ListItem>
											<Divider />
											{vendorList.map((v) => (
												<ListItemButton key={v.code} onClick={() => toggleVendor(v.code)}>
													<ListItemIcon>
														<Checkbox
															edge="start"
															checked={v.checked}
															tabIndex={-1}
															disableRipple
															inputProps={{ 'aria-labelledby': `checkbox-list-label-${v.code}` }}
														/>
													</ListItemIcon>
													<ListItemText id={`checkbox-list-label-${v.code}`} primary={v.name} />
												</ListItemButton>
											))}
											<Divider />
										</List>
									</Box>
								</Popover>
							</Box>
							<Button variant="contained" color="success" onClick={handleExportExcel}>
								Descargar Excel
							</Button>

						</Stack>
					</Paper>

					<Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
						<Tabs
							value={activeReportTab}
							onChange={(_, v) => setActiveReportTab(v)}
							indicatorColor="primary"
							textColor="primary"
						>
							<Tab value="visits" label="Efectividad de visitas" />
							<Tab value="orders" label="Detalle de visitas" />
							<Tab value="viewVisit" label="Vista visita" />
							<Tab value="viewCustomer" label="Vista clientes" />
						</Tabs>
					</Box>

					<Box sx={{ flex: 1, overflow: 'auto' }}>
						{loader ? (
							<Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
								<CircularProgress />
							</Box>
						) :
							<>
								{activeReportTab === "visits" && <CustomersVisitControl dataReport={dataReport.ordersResume || []} details={dataReport.configurationsDays} />}
								{activeReportTab === "orders" && <SellersOrders dataReport={dataReport.details || []} orderResume={dataReport.ordersResume || []} sellerSelected={selectedVendors} />}
								{activeReportTab === "viewCustomer" && <ViewCostumer data={dataReport.details || []} />}
								{activeReportTab === "viewVisit" && <ViewVisit data={dataReport.details || []} />}
							</>
						}
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};
